import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

// Normalize API base URL to always include /api once
const rawBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const baseURL = /\/api\/?$/i.test(rawBase)
  ? rawBase.replace(/\/$/, '')
  : rawBase.replace(/\/$/, '') + '/api';

// Controle de notificações para evitar duplicações
const errorNotifications = {
  lastMessages: {} as Record<string, number>,
  debounceTime: 2000, // 2 segundos entre notificações iguais
  
  showError(message: string, type: string = 'generic') {
    const now = Date.now();
    const key = `${type}:${message}`;
    
    // Se a mesma mensagem foi exibida recentemente, não exibe novamente
    if (this.lastMessages[key] && now - this.lastMessages[key] < this.debounceTime) {
      return;
    }
    
    // Registra o timestamp desta notificação
    this.lastMessages[key] = now;
    toast.error(message);
  }
};

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log de debug para todas as requisições (removido para produção)

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Handle 401 errors (unauthorized) - mas só para requests autenticados
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Se é um request de login, não tenta refresh - apenas rejeita
      if (originalRequest.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post('/api/auth/refresh', {
            refreshToken,
          });

          if (response.data.success) {
            const { token, refreshToken: newRefreshToken } = response.data.data;
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', newRefreshToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Token refresh failed
        }
      }

      // Se refresh falha, limpa tokens e força redirecionamento para login
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');

      // Redireciona para a página de login
      window.location.href = '/login';

      // Não propaga o erro quando já estamos redirecionando
      return Promise.resolve();
    }

    // Handle 403 errors (forbidden) - pode ser token expirado também
    if (error.response?.status === 403 && !originalRequest._retry) {
      const hasToken = localStorage.getItem('token');

      // Se não tem token, provavelmente é sessão expirada
      if (!hasToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.resolve();
      }

      // Se tem token, é realmente acesso negado
      originalRequest._retry = true;
    }

    // Handle other errors - but skip generic server error for auth routes (handled by AuthContext)
    const errorData = error.response?.data as any;
    const isMetaApiError = errorData?.code === 'META_API_ERROR' && errorData?.requiresSupport;
    const isAuthRoute = originalRequest?.url?.includes('/auth/');

    if (error.response && error.response.status >= 500 && !isMetaApiError && !isAuthRoute) {
      errorNotifications.showError('Erro interno do servidor. Tente novamente mais tarde.', 'server');
    } else if (error.response?.status === 403 && !isAuthRoute) {
      // Só mostra erro de permissão se o usuário estiver autenticado
      const hasToken = localStorage.getItem('token');
      if (hasToken) {
        errorNotifications.showError('Acesso negado. Você não tem permissão para esta ação.', 'forbidden');
      }
    } else if (error.response?.status === 404 && !isAuthRoute) {
      errorNotifications.showError('Recurso não encontrado.', 'notfound');
    } else if (error.code === 'ECONNABORTED') {
      errorNotifications.showError('Tempo limite excedido. Verifique sua conexão.', 'timeout');
    } else if (!error.response) {
      errorNotifications.showError('Erro de conexão. Verifique sua internet.', 'connection');
    }

    return Promise.reject(error);
  }
);

// API service functions
export const authService = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  register: (data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
  }) => api.post('/auth/register', data),
  
  logout: () => api.post('/auth/logout'),
  
  me: () => api.get('/auth/me'),
  
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
};

export const userService = {
  getProfile: () => api.get('/profile'),
  
  updateProfile: (data: { name?: string; phone?: string }) =>
    api.put('/profile', data),
  
  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => api.post('/profile/change-password', data),
  
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/profile/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  removeAvatar: () => api.delete('/profile/avatar'),
  
  getStats: () => api.get('/profile/stats'),
  
  testEmail: () => api.post('/profile/test-email'),
};

export const servicesService = {
  getServices: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    plan?: string;
  }) => api.get('/services', { params }),

  getService: (id: number) => api.get(`/services/${id}`),

  createService: (data: {
    server_id: number;
    ramal: string;
    ip: string;
    password: string;
    plan: string;
    description?: string;
  }) => api.post('/services', data),

  updateService: (id: number, data: {
    ip?: string;
    password?: string;
    plan?: string;
    status?: string;
    description?: string;
  }) => api.put(`/services/${id}`, data),

  suspendService: (id: number) => api.post(`/services/${id}/suspend`),

  releaseService: (id: number) => api.post(`/services/${id}/release`),

  deleteService: (id: number) => api.delete(`/services/${id}`),
};

export const serversService = {
  getServers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) => api.get('/servers', { params }),
  
  getServer: (id: number) => api.get(`/servers/${id}`),
  
  createServer: (data: {
    name: string;
    url: string;
    client_id: string;
    client_secret: string;
    description?: string;
  }) => api.post('/servers', data),
  
  updateServer: (id: number, data: {
    name?: string;
    url?: string;
    client_id?: string;
    client_secret?: string;
    description?: string;
    is_active?: boolean;
  }) => api.put(`/servers/${id}`, data),
  
  deleteServer: (id: number) => api.delete(`/servers/${id}`),
  
  testServer: (id: number) => api.post(`/servers/${id}/test`),
};

export const invoicesService = {
  getInvoices: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => api.get('/invoices', { params }),
  
  getInvoice: (id: number) => api.get(`/invoices/${id}`),
  
  getStats: () => api.get('/invoices/stats/summary'),
  
  getClientInvoices: (email: string, status?: string) =>
    api.get(`/invoices/client/${email}`, { params: { status } }),
  
  testConnection: () => api.post('/invoices/test-connection'),
};

export const callReportsService = {
  getCallReports: (params?: {
    page?: number;
    limit?: number;
    service_id?: number;
    direction?: string;
    disposition?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }) => api.get('/call-reports', { params }),

  getCallReport: (id: number) => api.get(`/call-reports/${id}`),

  getStatistics: (params?: {
    start_date?: string;
    end_date?: string;
    service_id?: number;
  }) => api.get('/call-reports/statistics/summary', { params }),

  getDashboardStatistics: () => api.get('/call-reports/statistics/dashboard'),

  getDailyStatistics: (params?: {
    days?: number;
    service_id?: number;
  }) => api.get('/call-reports/statistics/daily', { params }),

  reprocessFailed: () => api.post('/call-reports/reprocess-failed'),
};

export const adminService = {
  getUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) => api.get('/admin/users', { params }),

  getUser: (id: number) => api.get(`/admin/users/${id}`),

  updateUser: (id: number, data: {
    name?: string;
    email?: string;
    phone?: string;
    is_active?: boolean;
    is_admin?: boolean;
  }) => api.put(`/admin/users/${id}`, data),

  deleteUser: (id: number) => api.delete(`/admin/users/${id}`),

  getDashboardStats: () => api.get('/admin/stats'),
};

// Protected video service - uses direct axios instance to avoid baseURL conflicts
export const videoService = {
  getProtectedVideo: (filename: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    // Use full URL to avoid baseURL conflicts with frontend
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const fullUrl = `${apiUrl.replace('/api', '')}/api/protected-video/${filename}`;

    return axios.get(fullUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob', // Important for video files
      timeout: 30000,
    });
  },
};

export { api };